import * as React from 'react';

import { cn } from '@/shared/lib/utils';

export interface PageContainerProps {
  /** Max width of the inner content. Defaults to `1600px` — utilises the full
   *  width on wide monitors without stretching uncomfortably. Pass `none` to
   *  go truly edge-to-edge. */
  maxWidth?: '1200' | '1400' | '1600' | '1800' | 'none';
  /** Vertical padding intensity. Default `default` (py-6). `compact` = py-4. */
  density?: 'default' | 'compact' | 'spacious';
  children: React.ReactNode;
  className?: string;
}

const maxWidthClass: Record<NonNullable<PageContainerProps['maxWidth']>, string> = {
  '1200': 'tw-max-w-[1200px]',
  '1400': 'tw-max-w-[1400px]',
  '1600': 'tw-max-w-[1600px]',
  '1800': 'tw-max-w-[1800px]',
  none: '',
};

const densityClass: Record<NonNullable<PageContainerProps['density']>, string> = {
  // No top padding — the outer `.page-content` already provides 58px of
  // top margin below the nav bar, so any additional top padding here
  // just wastes vertical space. Bottom padding stays for breathing
  // room before the page footer.
  compact: 'tw-pt-0 tw-pb-2',
  default: 'tw-pt-0 tw-pb-3',
  spacious: 'tw-pt-0 tw-pb-5',
};

/**
 * PageContainer — the outer wrapper for every route component.
 *
 * Replaces the `page-content` div + ad-hoc `max-w-3xl/5xl/7xl` containers that
 * various agents used. Default `1600px` max-width utilises ultra-wide monitors
 * without stretching forms past readable line length.
 *
 * @example
 *   <PageContainer>
 *     <PageHeader title="LEADS" ... />
 *     <SectionCard ...>...</SectionCard>
 *   </PageContainer>
 */
export function PageContainer({
  maxWidth = '1600',
  density = 'default',
  children,
  className,
}: PageContainerProps) {
  return (
    <div className="page-content">
      <div
        className={cn(
          'tw-mx-auto tw-w-full tw-px-4 sm:tw-px-6',
          densityClass[density],
          maxWidthClass[maxWidth],
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
